import { Grid, HStack, Stack, Text } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '$/components/common/Icon';
import { OverflowButton } from '$/components/common/OverflowButton';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { useToast } from '$/hooks/useToast';
import { type User } from '$/services/usecases/authentication/mapper/genericCloudResponse';
import { extendTeacherExpiry } from '$/services/usecases/school/schools';

interface Props {
  teacher: User;
  onDelete: (teacher: User) => void;
}

export const TeacherCard: FC<Props> = ({ onDelete, teacher }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const schoolId = useAuthenticationStore.useUser()?.schoolId;

  const { mutate: extendExpiry, isPending } = useMutation({
    mutationFn: () =>
      extendTeacherExpiry({ schoolId: schoolId!, teacherId: teacher.id }),
    onSuccess: (data) => {
      if (!data.isSuccessful) return;
      toast(t('school.teachers.extendExpirySuccess'), 'success', '');
    },
  });

  const canExtend =
    teacher.role !== 'superTeacher' &&
    (!teacher.expirationDate ||
      dayjs(teacher.expirationDate).diff(dayjs(), 'day') <= 365);
  const canDelete = teacher.role !== 'superTeacher';

  return (
    <Grid
      alignItems='center'
      templateColumns='minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)'
      w='full'
      px='6'
      py='4'
      border='1px solid'
      borderColor='border'
      borderRadius='md'
    >
      <Stack gap='0'>
        <Text color='lighterText' fontSize='md'>
          {teacher.email}
        </Text>
        <Text fontSize='md' fontWeight='bold'>
          {teacher.firstname} {teacher.lastname}
        </Text>
      </Stack>
      <Text>{teacher.expirationDate ?? '-'}</Text>
      <HStack justifyContent='flex-end'>
        <OverflowButton
          px='6'
          fontSize='sm'
          isDisabled={!canExtend}
          isLoading={isPending}
          leftIcon={<Icon icon='refresh' w='14px' />}
          onClick={() => extendExpiry()}
          variant='tertiary'
        >
          {t('school.teachers.extend')}
        </OverflowButton>
        <OverflowButton
          px='6'
          fontSize='sm'
          isDisabled={!canDelete}
          leftIcon={<Icon icon='trash_can' w='14px' />}
          onClick={() => onDelete(teacher)}
          variant='tertiary'
          hideTextOnOverflow
        >
          {t('general.delete')}
        </OverflowButton>
      </HStack>
    </Grid>
  );
};
